import type { TowerDesign } from "@/game/towerSpec";

/**
 * Heavy-duty 70 m self-supporting 4-legged lattice (à la a tall ROHN SSV / Sabre
 * self-support carrying a full microwave + panel load). Owned by the lattice agent.
 *
 * Showcases the heavier section / higher-grade options:
 *  - Legs: 8" Sch-40 pipe (legXXL, I = 72.5 in⁴, A = 54.2 cm²) in A992 steel
 *    (Fy 345 MPa). With 3.5 m panels the Euler buckling capacity is ~4860 kN —
 *    so high that the yield cap (A·Fy ≈ 1870 kN) governs. That's the realistic
 *    buckling→yield crossover: a stocky enough column no longer buckles, it
 *    yields. The leg is the failure element either way.
 *  - Bracing: L6×6×3/8 angles (angleL6) — the largest angle in the catalog,
 *    needed because a 70 m tower's shear and panel forces are large.
 *
 * Geometry / rationale:
 *  - Base width 10 m at 70 m tall → base/height ≈ 1/7, real self-supporting
 *    proportion (docs/ENGINEERING.md §3.2), inside the 1/6–1/12 range.
 *  - 20 panels → 3.5 m panel height, keeping legs stocky (buckling well above
 *    the design-wind force) and below the validator's 6 m warn threshold.
 *  - Heavy taper (baseR 5.0 → topR 1.4) matches real tall SSV towers.
 *  - Peak leg utilization: ~5% dead-only, ~50% at the 40 m/s TIA design gust,
 *    ~90% near 55 m/s, failing (windward leg yields → topple) past ~58 m/s.
 *    Comfortable standing headroom, plausible high-wind failure.
 */
export const design: TowerDesign = {
  id: "lattice-heavy-70m",
  name: "Heavy Lattice",
  description: "70 m heavy-duty self-supporting tower — 8\" pipe legs, A992, L6 bracing.",
  spec: {
    type: "lattice",
    height: 70,
    stack: {
      faces: 4,
      panels: 20, // 3.5 m panels — legs stay stocky (yield, not buckling, governs)
      baseRadius: 5.0, // base width 10 m ≈ height/7 (real tall-SSV proportion)
      topRadius: 1.4,
      legSection: "legXXL", // 8" Sch-40 pipe — Pcr ≈ 4860 kN, Pyield ≈ 1870 kN
      braceSection: "angleL6", // L6×6×3/8 — heaviest angle, for the high panel shear
      legMaterial: "a992", // A992 structural steel (Fy 345 MPa)
      braceMaterial: "a572", // A572 Gr50 angles
      bracing: "X",
    },
  },
};
